import { useCallback, useEffect, useRef } from 'react'
import { type AudioCue, TrideAudioEngine } from '../audio/audio-engine'
import type { GameRuntime } from '../game/runtime'

const AUDIO_EVENTS = new Set<AudioCue>([
  'fire',
  'hit',
  'driver-hit',
  'reload-start',
  'reload-complete',
  'vehicle-contact',
  'drift-bank',
  'boost',
  'lap',
  'finish',
  'reset',
])

export interface AudioControls {
  unlockAudio: () => boolean
}

export function useAudio(runtime: GameRuntime, enabled: boolean, muted: boolean): AudioControls {
  const engineRef = useRef<TrideAudioEngine | null>(null)
  const lastEventId = useRef(0)
  const contactCooldown = useRef(new Map<string, number>())
  if (!engineRef.current) engineRef.current = new TrideAudioEngine()

  const unlockAudio = useCallback(() => engineRef.current?.unlock() ?? false, [])

  useEffect(() => {
    const unlock = () => {
      unlockAudio()
    }
    window.addEventListener('pointerdown', unlock, { capture: true })
    window.addEventListener('touchend', unlock, { capture: true })
    window.addEventListener('keydown', unlock, { capture: true })
    return () => {
      window.removeEventListener('pointerdown', unlock, true)
      window.removeEventListener('touchend', unlock, true)
      window.removeEventListener('keydown', unlock, true)
    }
  }, [unlockAudio])

  useEffect(() => {
    engineRef.current?.setMuted(muted)
  }, [muted])

  useEffect(() => {
    const engine = engineRef.current
    if (!enabled || !engine?.start()) return
    let frame = 0
    const update = () => {
      const state = runtime.getState()
      if (state.nextEventId <= lastEventId.current) {
        lastEventId.current = 0
        contactCooldown.current.clear()
        engine.resetRace()
      }
      const player = state.vehicles.find((vehicle) => vehicle.id === 'player')
      if (player) {
        const motionAudible = !runtime.isPaused() && state.race.phase !== 'finished'
        engine.update(
          motionAudible ? player.speed : 0,
          motionAudible && player.drift.active,
          motionAudible && player.boostActive,
        )
      }
      for (const event of state.events) {
        if (event.id <= lastEventId.current) continue
        lastEventId.current = Math.max(lastEventId.current, event.id)
        const cue: AudioCue | null =
          event.type === 'vehicle-explode'
            ? 'reset'
            : event.type === 'rail-break'
              ? 'vehicle-contact'
              : AUDIO_EVENTS.has(event.type as AudioCue)
                ? (event.type as AudioCue)
                : null
        if (!cue) continue
        const relevant = event.vehicleId === 'player' || event.targetId === 'player' || event.type === 'finish'
        if (!relevant) continue
        if (event.type === 'vehicle-contact' && event.targetId) {
          const pair = [event.vehicleId, event.targetId].sort().join(':')
          const previous = contactCooldown.current.get(pair) ?? Number.NEGATIVE_INFINITY
          if (state.time - previous < 0.22) continue
          contactCooldown.current.set(pair, state.time)
        }
        engine.trigger(cue)
      }
      frame = requestAnimationFrame(update)
    }
    frame = requestAnimationFrame(update)
    return () => cancelAnimationFrame(frame)
  }, [enabled, runtime])

  useEffect(() => () => engineRef.current?.dispose(), [])

  return { unlockAudio }
}
